import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signUp } from 'aws-amplify/auth';
import { getCurrentUserType } from '../amplify-config';
import { Eye, EyeOff, Copy } from 'lucide-react';

interface SignupFormData {
  email: string;
  password: string;
  region: string;
  organisationName?: string;  // For business users only
}

const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SignupFormData>({
    email: '',
    password: '',
    region: '',
    organisationName: '',
  });
  const [errors, setErrors] = useState<Partial<SignupFormData>>({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Get user type from localStorage
  const userType = getCurrentUserType();

  // Regions for bucket creation
  const regions = [
    'United States',
    'Northern Europe',
    'Southern Europe',
    'Central Europe',
    'Western Europe',
    'United Kingdom',
    'Asia Pacific',
    'Africa',
    'Australia (Oceania)',
    'Canada',
    'Middle East',
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(formData.password);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<SignupFormData> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }

    // Region required
    if (!formData.region) {
      newErrors.region = 'Region is required';
    }

    // Organisation name validation for business users
    if (userType === 'business') {
      if (!formData.organisationName) {
        newErrors.organisationName = 'Organisation name is required for business accounts';
      } else if (formData.organisationName.length < 2) {
        newErrors.organisationName = 'Organisation name must be at least 2 characters';
      } else if (formData.organisationName.length > 100) {
        newErrors.organisationName = 'Organisation name must be 100 characters or less';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      const userAttributes: Record<string, string> = {
        email: formData.email,
        'custom:region': formData.region,
        'custom:profileComplete': 'incomplete',
      };

      // Add organisation name for business users
      if (userType === 'business' && formData.organisationName) {
        userAttributes['custom:organisationName'] = formData.organisationName;
      }

      await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes,
        },
      });

      // Redirect to verification page
      navigate('/verify', { state: { email: formData.email } });
    } catch (error: any) {
      console.error('Signup error:', error);
      setGeneralError(error.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <h2 className="form-title">Create Account</h2>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
          />
          {errors.email && <span className="form-error">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="password">Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              className={`form-input ${errors.password ? 'error' : ''}`}
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              style={{ paddingRight: '80px' }}
            />
            <div style={{ 
              position: 'absolute', 
              right: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '4px'
            }}>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666'
                }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              <button
                type="button"
                onClick={handleCopyPassword}
                disabled={!formData.password}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: formData.password ? 'pointer' : 'not-allowed',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: copySuccess ? '#4caf50' : '#666',
                  opacity: formData.password ? 1 : 0.4
                }}
                title={copySuccess ? 'Copied!' : 'Copy password'}
              >
                <Copy size={20} />
              </button>
            </div>
          </div>
          {errors.password && <span className="form-error">{errors.password}</span>}
          <small className="form-hint">Min 8 characters with uppercase, lowercase, number, and special character</small>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="region">Data Crate Region</label>
          <select
            id="region"
            name="region"
            className={`form-input ${errors.region ? 'error' : ''}`}
            value={formData.region}
            onChange={handleChange}
          >
            <option value="">Select your region</option>
            {regions.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
          {errors.region && <span className="form-error">{errors.region}</span>}
          <small className="form-hint">Your data crates will be created in this region</small>
        </div>

        {/* Organisation Name - Only for Business Users */}
        {userType === 'business' && (
          <div className="form-group">
            <label className="form-label" htmlFor="organisationName">Organisation Name</label>
            <input
              type="text"
              id="organisationName"
              name="organisationName"
              className={`form-input ${errors.organisationName ? 'error' : ''}`}
              value={formData.organisationName}
              onChange={handleChange}
              maxLength={100}
              placeholder="Your Company Name"
            />
            {errors.organisationName && <span className="form-error">{errors.organisationName}</span>}
            <small className="form-hint">2-100 characters</small>
          </div>
        )}

        {generalError && <div className="form-error">{generalError}</div>}

        <button 
          type="submit" 
          className="btn btn-primary" 
          disabled={loading} 
          style={{ width: '100%', marginTop: '1rem' }}
        >
          {loading ? <span className="loading-spinner"></span> : 'Create Account'}
        </button>
      </form>
    </div>
  );
};

export default SignupForm;
